import styled from '@emotion/styled'
import {COLORS} from '../constants'

/** A titled block below the KPI tiles, matching the detail sections' spacing. */
export const Section = styled.section`
  margin-top: 14px;
`

/** Small uppercase caption heading the table, matching the KPI panel headings. */
export const SectionHeading = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${COLORS.mutedText};
  margin-bottom: 8px;
`

/**
 * Horizontal scroll container so the five columns stay reachable on a narrow card rather than
 * wrapping or dropping a column — the table keeps its `min-width` and this scrolls it.
 */
export const ScrollArea = styled.div`
  overflow-x: auto;
`

/** The contributions table. */
export const Table = styled.table`
  width: 100%;
  min-width: 420px;
  border-collapse: collapse;
  font-size: 12px;
`

/** Column-header row. */
export const HeadRow = styled.tr`
  text-align: left;
  color: ${COLORS.mutedText};
`

type CellProps = {align?: 'left' | 'right'}

/** Column header cell. */
export const Th = styled.th<CellProps>`
  padding: 6px 10px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-align: ${({align}) => align ?? 'left'};
  border-bottom: 1px solid ${COLORS.border};
  white-space: nowrap;
`

/**
 * One contributor row. A row that points at a mounted child card is interactive (hover
 * background, pointer cursor); a row with no scroll target stays inert.
 */
export const Row = styled.tr<{clickable: boolean}>`
  cursor: ${({clickable}) => (clickable ? 'pointer' : 'default')};

  &:hover {
    background-color: ${({clickable}) => (clickable ? COLORS.mutedBg : 'transparent')};
  }
`

/** Body cell. */
export const Td = styled.td<CellProps>`
  padding: 6px 10px;
  text-align: ${({align}) => align ?? 'left'};
  border-bottom: 1px solid ${COLORS.border};
  white-space: nowrap;
`

/** Owner cell, emphasised. The name inside clamps its own width (see {@link OwnerName}/{@link ScrollButton}). */
export const OwnerCell = styled(Td)`
  font-weight: 500;
  color: ${COLORS.foreground};
`

/** Plain (non-clickable) owner name; clamps a long name to an ellipsis (full name via `title`). */
export const OwnerName = styled.span`
  display: inline-block;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
`

/**
 * The owner name of a sub-agent row, rendered as a real `<button>` so it is a proper, keyboard-
 * operable control (Enter/Space activate it natively) that scrolls the tree to that child — the
 * same in-cell-button pattern the Agent Chain breadcrumb and investor book use, rather than an
 * ARIA role bolted onto the `<tr>` (which would break the table's row/cell semantics).
 */
export const ScrollButton = styled.button`
  display: inline-block;
  max-width: 160px;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-weight: 500;
  color: ${COLORS.brand};
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.brand};
    outline-offset: 2px;
    border-radius: 3px;
  }
`

/** Muted role cell. */
export const RoleCell = styled(Td)`
  color: ${COLORS.mutedText};
`

/** Funds cell, emphasised in the success colour to match the card's money figures. */
export const FundsCell = styled(Td)`
  font-weight: 600;
  color: ${COLORS.success};
`

/** Sticky-feeling totals row summing funds (and a 100% share). */
export const TotalRow = styled.tr`
  font-weight: 600;
  color: ${COLORS.foreground};

  & > td {
    padding: 6px 10px;
    border-top: 1px solid ${COLORS.border};
  }
`

/** "Total" caption in the footer's first cell. */
export const TotalLabel = styled.td`
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${COLORS.mutedText};
`

/** Shown instead of the table when the node has no direct children. */
export const EmptyState = styled.div`
  padding: 12px 0;
  font-size: 12px;
  color: ${COLORS.mutedText};
`
